import { useEffect, useMemo, useState } from 'react';

import { Option } from 'common/components/Inputs/Autocomplete/utils/models';
import { useMnosByIds } from './useMnosByIds';
import { useInfinityMnosQuery } from './useInfinityMnosQuery';
import { OPTION_TYPE } from 'src/common/components/Inputs/Autocomplete/utils/schemas';
import { MNO } from '../models/mnos';
import { useSaveOnMountArrayState } from 'common/hooks/useSaveOnMountArrayState.ts';

type MNOOption = Option & { mnoId: number | null };

type UseMnosAutocompleteProps = {
  initialIds: (string | null)[];
  search: string;
  hints: string[];
  onSelectedChange: (options: MNOOption[]) => void;
};

const blankOptionMnoName = 'blank';

type MnoRefWithBlank = MNO | { id: null; ccii: null; name: string };

const mapMnoToOption = (mnos: MnoRefWithBlank[]) => {
  return mnos.map((mno) => {
    if (mno.ccii === null) {
      return { id: null, mnoId: null, name: blankOptionMnoName, type: OPTION_TYPE.BLANK };
    }
    return {
      id: mno.ccii,
      name: mno.name,
      mnoId: mno.id,
    };
  });
};

export const useMnosAutocomplete = ({
  search,
  initialIds,
  hints,
  onSelectedChange,
}: UseMnosAutocompleteProps) => {
  const displayHints = !search;
  const initialSelectedIds = useSaveOnMountArrayState(initialIds);

  const {
    mnos: mnosData,
    isFetched,
    ...mnosQuery
  } = useInfinityMnosQuery(search, {
    minTime: 250,
  });

  const {
    selectedMnos: initialSelectedMnos,
    isPending: isIntialFetching,
    isFetched: isIntialFetched,
  } = useMnosByIds(initialSelectedIds.filter((id) => id !== null));

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const shouldResetOptions = initialIds.length === 0 && selectedOptions.length !== 0;

  useEffect(() => {
    if (isIntialFetched) {
      setSelectedOptions(mapMnoToOption(initialSelectedMnos));
    }
  }, [initialSelectedMnos, isIntialFetched]);

  const updateSelectedOptions = (options: MNOOption[]) => {
    setSelectedOptions(options);
    onSelectedChange(options);
  };

  const { selectedMnos: hintedMnos, isFetched: isHintFetched } = useMnosByIds(hints);
  const hintOptions = useMemo(
    () => (displayHints ? mapMnoToOption(hintedMnos) : []),
    [hintedMnos, displayHints],
  );

  const options = useMemo(() => {
    const isBlankSearched = !!search && blankOptionMnoName.includes(search.trim());
    return mapMnoToOption([
      ...(isBlankSearched ? [{ id: null, ccii: null, name: blankOptionMnoName }] : []),
      ...mnosData,
    ]);
  }, [mnosData, search]);

  useEffect(() => {
    if (shouldResetOptions) {
      updateSelectedOptions([]);
    }
  }, [shouldResetOptions]);

  const optionsToReturn = useMemo(() => {
    return initialIds.includes(null) && !selectedOptions.some((option) => option.id === null)
      ? [{ id: null, name: blankOptionMnoName, type: OPTION_TYPE.BLANK }, ...selectedOptions]
      : selectedOptions;
  }, [initialIds, selectedOptions]);

  return {
    selectedOptions: optionsToReturn,
    isSelectedOptionFetching: isIntialFetching,
    hintOptions: hintOptions,
    updateSelectedOptions,
    options: options,
    isFetched: isFetched && isHintFetched,
    ...mnosQuery,
  };
};
